import catalogJson from '../../../config/drafting-commands.json';
import {
  draftingContextFromCase,
  filterContextBonus,
  matchesCommandFilters,
} from './commandFilters';
import type { DraftingCommandEntry } from './types';

/** Synthetic slash row — run free-text prompt with Lex Counsel. */
export const CUSTOM_SLASH_COMMAND_ID = '__lex_custom_prompt__';

type CatalogFile = {
  version?: number;
  commands: DraftingCommandEntry[];
};

const catalog = (catalogJson as CatalogFile).commands ?? [];

export function getAllCommands(): DraftingCommandEntry[] {
  return catalog;
}

function normalize(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

function tokenize(query: string): string[] {
  return normalize(query)
    .split(/[\s\-_/]+/)
    .filter((t) => t.length > 1);
}

export function isCustomSlashCommand(cmd: DraftingCommandEntry): boolean {
  return cmd.id === CUSTOM_SLASH_COMMAND_ID;
}

export function buildCustomSlashCommand(query: string): DraftingCommandEntry {
  const trimmed = query.trim();
  return {
    id: CUSTOM_SLASH_COMMAND_ID,
    type: 'draft',
    category: 'draft_document',
    label: trimmed ? `Run: ${trimmed}` : 'Custom prompt',
    command: '',
    slashAliases: [],
    priority: 200,
  };
}

function scoreSlashQuery(
  cmd: DraftingCommandEntry,
  ctx: ReturnType<typeof draftingContextFromCase>,
  rawQuery: string,
): number {
  const q = normalize(rawQuery);
  if (!q) return filterContextBonus(cmd, ctx) + (cmd.priority ?? 50);

  let score = filterContextBonus(cmd, ctx);
  const tokens = tokenize(q);
  const label = normalize(cmd.label);
  const aliases = (cmd.slashAliases ?? []).map(normalize);
  const haystacks = [label, normalize(cmd.command), ...aliases];

  for (const h of haystacks) {
    if (!h) continue;
    if (h === q || h.includes(q) || q.includes(h)) score = Math.max(score, 100);
    else if (h.startsWith(q) || q.startsWith(h)) score = Math.max(score, 85);
    else {
      const overlap = tokens.filter((t) => h.includes(t)).length;
      if (overlap) score = Math.max(score, 40 + overlap * 15);
    }
  }
  for (const kw of cmd.keywords ?? []) {
    const k = normalize(kw);
    if (k && (k.includes(q) || tokens.some((t) => k.includes(t)))) {
      score = Math.max(score, 35);
    }
  }
  return score;
}

function caseContextualSlashPool(
  pool: DraftingCommandEntry[],
  ctx: ReturnType<typeof draftingContextFromCase>,
  cap: number,
): DraftingCommandEntry[] {
  return [...pool]
    .map((cmd) => ({
      score: filterContextBonus(cmd, ctx) + (cmd.priority ?? 50),
      cmd,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, cap)
    .map((s) => s.cmd);
}

export function filterCommandsForSlash(
  query: string,
  limit = 12,
  caseData?: Record<string, unknown> | null,
): DraftingCommandEntry[] {
  const ctx = draftingContextFromCase(caseData);
  const pool = catalog.filter((cmd) => matchesCommandFilters(cmd, ctx));
  const trimmed = query.trim();
  const q = normalize(trimmed);

  if (!q) {
    return [...pool]
      .sort((a, b) => (b.priority ?? 50) - (a.priority ?? 50))
      .slice(0, limit);
  }

  const scored = pool
    .map((cmd) => ({ score: scoreSlashQuery(cmd, ctx, trimmed), cmd }))
    .filter((s) => s.score >= 30)
    .sort(
      (a, b) =>
        b.score - a.score || (b.cmd.priority ?? 50) - (a.cmd.priority ?? 50),
    );

  const seen = new Set<string>();
  const merged: DraftingCommandEntry[] = [];
  const add = (cmd: DraftingCommandEntry) => {
    if (seen.has(cmd.id)) return;
    seen.add(cmd.id);
    merged.push(cmd);
  };

  for (const { cmd } of scored) {
    if (merged.length >= limit - 1) break;
    add(cmd);
  }

  if (merged.length < 5) {
    for (const cmd of caseContextualSlashPool(pool, ctx, 6)) {
      if (merged.length >= 5) break;
      add(cmd);
    }
  }

  if (trimmed.length >= 2) {
    const custom = buildCustomSlashCommand(trimmed);
    return [custom, ...merged.filter((c) => c.id !== custom.id)].slice(0, limit);
  }

  return merged.slice(0, limit);
}

/** True when free text likely maps to a full-document drafting command in the catalog. */
export function matchDraftDocumentRequest(
  query: string,
  caseData?: Record<string, unknown> | null,
  minScore = 55,
): boolean {
  const trimmed = query.trim();
  if (!trimmed) return false;
  // A fuzzy catalog hit alone is not enough to open a new tab. In particular,
  // "correct" must not be interpreted as the document type "correction".
  const normalized = normalize(trimmed);
  const hasGenerationVerb =
    /\b(write|draft|create|prepare|generate|compose|draw up|file|frame)\b/.test(normalized);
  const hasDocumentTarget =
    /\b(application|plaint|petition|written statement|reply|replication|rejoinder|affidavit|appeal|memo(?:randum)?|suit|complaint|notice|bail|vakalatnama|caveat|objection|counter|synopsis|revision|review|reference|execution)\b/.test(
      normalized,
    );
  if (!hasGenerationVerb && !hasDocumentTarget) return false;
  const ctx = draftingContextFromCase(caseData);
  const pool = catalog.filter(
    (cmd) => cmd.category === 'draft_document' && matchesCommandFilters(cmd, ctx),
  );
  let best = 0;
  for (const cmd of pool) {
    best = Math.max(best, scoreSlashQuery(cmd, ctx, trimmed));
  }
  return best >= minScore;
}

export function getCommandById(id: string): DraftingCommandEntry | undefined {
  return catalog.find((c) => c.id === id || c.command === id);
}

export function requiresSelection(cmd: DraftingCommandEntry): boolean {
  return cmd.category === 'improve_selection' || cmd.type === 'improve';
}
